import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, BookOpen, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog`);
        if (response.ok) {
          const data = await response.json();
          setPosts(data);
        }
      } catch (error) {
        console.error('Failed to fetch posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [API_URL]);

  return (
    <div data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-cream-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-6 block">
              Resources
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-charcoal leading-tight mb-8">
              Blog
            </h1>
            <p className="text-xl text-charcoal/70 leading-relaxed">
              Articles on nutrition, therapy techniques, injury prevention, and wellness tips 
              to support your health journey.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-sage-600 animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-sage-200 mx-auto mb-4" />
              <h3 className="font-serif text-2xl text-charcoal mb-2">Coming Soon</h3>
              <p className="text-charcoal/60">
                Blog articles are being prepared. Check back soon!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/resources/blog/${post.slug}`}>
                    <Card className="h-full bg-white border-0 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-300 rounded-2xl overflow-hidden group">
                      <CardContent className="p-0">
                        {/* Thumbnail */}
                        <div className="bg-sage-100 aspect-video flex items-center justify-center overflow-hidden">
                          {post.featured_image ? (
                            <img 
                              src={post.featured_image.startsWith('/api') ? `${API_URL}${post.featured_image}` : post.featured_image} 
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <BookOpen className="w-12 h-12 text-sage-300" />
                          )}
                        </div>
                        
                        <div className="p-6">
                          <div className="flex items-center gap-4 mb-3">
                            <span className="text-xs font-semibold text-sage-600 uppercase tracking-wide">
                              {post.category}
                            </span>
                            <span className="flex items-center gap-1 text-xs text-charcoal/50">
                              <Clock className="w-3 h-3" />
                              {post.read_time}
                            </span>
                          </div>
                          
                          <h2 className="font-serif text-xl font-medium text-charcoal mb-3 group-hover:text-sage-600 transition-colors line-clamp-2">
                            {post.title}
                          </h2>
                          
                          <p className="text-charcoal/60 text-sm leading-relaxed mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>
                          
                          <span className="inline-flex items-center gap-2 text-sage-600 font-semibold text-sm group-hover:gap-3 transition-all">
                            Read More
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
